/**
 * Custom hook for managing document modal state and operations.
 */

import { useState, useCallback } from 'react';
import type { SavedDocument } from '@/lib/types';
import { addApi } from '@/services/addApi';
import { errorMessages } from '@/lib/errors';

interface UseDocumentModalOptions {
  updateDocument: (id: string, updates: Partial<SavedDocument>) => Promise<SavedDocument | null>;
  deleteDocument: (id: string) => Promise<boolean>;
  onDocumentsChange: () => Promise<void>;
}

interface UseDocumentModalResult {
  // Modal state
  selectedDoc: SavedDocument | null;
  editedContent: string;
  reviewMode: 'preview' | 'edit' | 'transcription';
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isDeleting: boolean;

  // Transcription state
  transcriptionContent: string | null;
  transcriptionLoading: boolean;
  transcriptionError: string | null;

  // Move state
  docToMove: SavedDocument | null;
  isMoving: boolean;

  // Handlers
  openDocument: (doc: SavedDocument) => void;
  openDocumentInEditMode: (doc: SavedDocument) => void;
  closeDocument: () => void;
  setReviewMode: (mode: 'preview' | 'edit' | 'transcription') => void;
  handleContentEdit: (content: string) => void;
  handleSaveDocument: () => Promise<void>;
  handleDeleteDocument: (docId: string) => Promise<void>;
  handleCheckboxToggle: (lineIndex: number, checked: boolean) => void;
  openMoveModal: (doc: SavedDocument) => void;
  closeMoveModal: () => void;
  handleMoveDocument: (docId: string, projectId: string, projectName: string) => Promise<void>;
}

export function useDocumentModal({
  updateDocument,
  deleteDocument,
  onDocumentsChange,
}: UseDocumentModalOptions): UseDocumentModalResult {
  const [selectedDoc, setSelectedDoc] = useState<SavedDocument | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');
  const [reviewMode, setReviewModeState] = useState<'preview' | 'edit' | 'transcription'>('preview');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Move state
  const [docToMove, setDocToMove] = useState<SavedDocument | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  // Transcription state
  const [transcriptionContent, setTranscriptionContent] = useState<string | null>(null);
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);

  // Fetch transcription from API
  const fetchTranscription = useCallback(async (transcriptionId: string) => {
    setTranscriptionLoading(true);
    setTranscriptionError(null);
    try {
      const result = await addApi.getTranscription(transcriptionId);
      setTranscriptionContent(result.content);
    } catch (err) {
      setTranscriptionError(errorMessages.getTranscription(err));
    } finally {
      setTranscriptionLoading(false);
    }
  }, []);

  // Set review mode with auto-fetch for transcription tab
  const setReviewMode = useCallback((mode: 'preview' | 'edit' | 'transcription') => {
    setReviewModeState(mode);
    if (mode === 'transcription' && selectedDoc?.transcriptionId && transcriptionContent === null && !transcriptionLoading) {
      fetchTranscription(selectedDoc.transcriptionId);
    }
  }, [selectedDoc, transcriptionContent, transcriptionLoading, fetchTranscription]);

  // Open a document for review/edit
  const openDocument = useCallback((doc: SavedDocument) => {
    setSelectedDoc(doc);
    setEditedContent(doc.content);
    setReviewModeState('preview');
    setHasUnsavedChanges(false);
    setTranscriptionContent(null);
    setTranscriptionLoading(false);
    setTranscriptionError(null);
  }, []);

  // Open a document directly in edit mode
  const openDocumentInEditMode = useCallback((doc: SavedDocument) => {
    setSelectedDoc(doc);
    setEditedContent(doc.content);
    setReviewModeState('edit');
    setHasUnsavedChanges(false);
    setTranscriptionContent(null);
    setTranscriptionLoading(false);
    setTranscriptionError(null);
  }, []);

  // Move modal handlers
  const openMoveModal = useCallback((doc: SavedDocument) => {
    setDocToMove(doc);
  }, []);

  const closeMoveModal = useCallback(() => {
    setDocToMove(null);
  }, []);

  const handleMoveDocument = useCallback(
    async (docId: string, projectId: string, projectName: string) => {
      if (isMoving) return;

      setIsMoving(true);
      try {
        await updateDocument(docId, { projectId, projectName });
        await onDocumentsChange();
        setDocToMove(null);
      } catch (err) {
        console.error('Failed to move document:', err);
      } finally {
        setIsMoving(false);
      }
    },
    [isMoving, updateDocument, onDocumentsChange]
  );

  // Close the document modal
  const closeDocument = useCallback(() => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    setSelectedDoc(null);
    setEditedContent('');
    setReviewModeState('preview');
    setHasUnsavedChanges(false);
    setTranscriptionContent(null);
    setTranscriptionLoading(false);
    setTranscriptionError(null);
  }, [hasUnsavedChanges]);

  // Handle content edit
  const handleContentEdit = useCallback((newContent: string) => {
    setEditedContent(newContent);
    setHasUnsavedChanges(true);
  }, []);

  // Save document changes
  const handleSaveDocument = useCallback(async () => {
    if (!selectedDoc || !editedContent || isSaving) return;

    setIsSaving(true);
    try {
      const updated = await updateDocument(selectedDoc.id, { content: editedContent });
      if (updated) {
        setSelectedDoc(updated);
        setHasUnsavedChanges(false);
        await onDocumentsChange();
      }
    } catch (err) {
      console.error('Failed to save document:', err);
    } finally {
      setIsSaving(false);
    }
  }, [selectedDoc, editedContent, isSaving, updateDocument, onDocumentsChange]);

  // Toggle a checkbox in the markdown content
  const handleCheckboxToggle = useCallback((lineIndex: number, checked: boolean) => {
    const lines = editedContent.split('\n');
    if (lineIndex < 0 || lineIndex >= lines.length) return;

    const line = lines[lineIndex];
    if (checked) {
      lines[lineIndex] = line.replace('- [ ] ', '- [x] ');
    } else {
      lines[lineIndex] = line.replace('- [x] ', '- [ ] ');
    }

    setEditedContent(lines.join('\n'));
    setHasUnsavedChanges(true);
  }, [editedContent]);

  // Delete a document
  const handleDeleteDocument = useCallback(
    async (docId: string) => {
      if (isDeleting) return;
      if (!window.confirm('Are you sure you want to delete this document?')) return;

      setIsDeleting(true);
      try {
        await deleteDocument(docId);
        await onDocumentsChange();
        if (selectedDoc?.id === docId) {
          setSelectedDoc(null);
        }
      } catch (err) {
        console.error('Failed to delete document:', err);
      } finally {
        setIsDeleting(false);
      }
    },
    [isDeleting, deleteDocument, onDocumentsChange, selectedDoc]
  );

  return {
    selectedDoc,
    editedContent,
    reviewMode,
    hasUnsavedChanges,
    isSaving,
    isDeleting,
    docToMove,
    isMoving,
    transcriptionContent,
    transcriptionLoading,
    transcriptionError,
    openDocument,
    openDocumentInEditMode,
    closeDocument,
    setReviewMode,
    handleContentEdit,
    handleSaveDocument,
    handleDeleteDocument,
    handleCheckboxToggle,
    openMoveModal,
    closeMoveModal,
    handleMoveDocument,
  };
}
